import { IComponentOptions, IPromise, IScope } from "angular";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../../../../../../records/forms/formView";
import { StateService } from "@uirouter/angularjs";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../../../records/forms/formly/fieldConfigurationHelpers";
import {
  RecordsUtilsService,
  vsName as RecordsUtilsServiceName,
} from "../../../../../../records/utils";
import { RecordFileTemplateOptions } from "../../../../../../records/forms/formly/types";
import fieldHelpers from "../../../../../../ui-formly/fieldHelpers";
import { MachineConfigurationView } from "../../../../models/machineConfiguration";
import { cloneDeep } from "lodash";
import { IFieldArray, ITemplateScope } from "AngularFormly";
import * as lookupNames from "../../../../configs/lookup";
import { LookupManagementService } from "../../../../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName } from "../../../../../shared/lookups";
import { Record } from "../../../../../../records/server/crud";
import {
  ChangeJournalModalService,
  vsServiceName as ChangeJournalModalServiceName,
} from "../../../../../../records/changeJournal/changeNoteModalService";

interface IMultipleMoveFormModel {
  MachineChanges: IMachineMoveRequest[];
  Date?: Date;
  Files: File[];
  Notes?: string;
}

interface IMachineMoveRequest {
  MachineId: number;
  Section?: string;
  Bank?: string;
  Slot?: string;
  SlotNumber?: string;
  MachineConfigurationTypeId?: number;
  MachineConfigurationType?: Record;
  BankSize?: string;
  Position?: string;
  MachineBankType?: Record;
  MachineBankTypeId?: number;
  /* TODO - add any client specific move fields here */
}

export class MachinesMoveViewComponentController {
  /* bindings */
  machineConfigurations!: MachineConfigurationView[];
  /* end bindings */

  model!: IMultipleMoveFormModel;
  machineInfo!: {
    machineConfigurations: MachineConfigurationView[];
  };
  formFields!: IFieldArray;

  static $inject = [
    FormViewServiceName,
    "$state",
    FieldConfigurationHelperServiceName,
    "$scope",
    RecordsUtilsServiceName,
    vsLookupManagementServiceName,
    ChangeJournalModalServiceName,
  ];
  constructor(
    private readonly formViewService: FormViewService,
    private readonly $state: StateService,
    private readonly fieldConfigurationHelperService: FieldConfigurationHelperService,
    $scope: IScope,
    private readonly recordUtils: RecordsUtilsService,
    private readonly lookupManagementService: LookupManagementService,
    private readonly changeJournalModalService: ChangeJournalModalService
  ) {
    $scope.$on("copyDown", (e, modelClicked) => {
      e.preventDefault();
      this.copyDown(modelClicked);
    });
  }

  private initializeFormFields() {
    this.formFields = [
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleDateField({
            key: "Date",
            name: "Date",
            label: "Change Date",
            required: true,
          }),
        ],
        4
      ),
      this.fieldConfigurationHelperService.repeaterField({
        key: "MachineChanges",
        name: "MachineChanges",
        allowAdd: false,
        allowAddBottom: false,
        displayNameSingular: "Machine",
        repeaterFields: [
          {
            className: "row",
            fieldGroup: [
              fieldHelpers.simpleReadonlyField({
                key: "MachineId",
                className: "col-xs-3",
                label: "Serial Number",
                formatters: [
                  (machineId) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        <number>machineId
                      );
                    return machineConfigurationView?.SerialNumber;
                  },
                ],
              }),
              this.fieldConfigurationHelperService.selectAsyncField({
                key: "MachineConfigurationTypeId",
                label: "IGT TYPE ID",
                required: true,
                queryUrl: "/api/machines/MachineConfigurationType",
                expressionProperties: {
                  "templateOptions.helpText": (
                    $viewValue,
                    $modelValue,
                    $scope
                  ) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        $scope.model?.MachineId
                      );
                    const section =
                      machineConfigurationView?.MachineConfigurationTypeName;
                    return section ? `Previous: ${section}` : "";
                  },
                },
                serverItemMapper: (model: any) => ({
                  Id: model.Id,
                  Name: model.MachineConfigurationTypeName,
                }),
                queryBuilder: (search, model) => ({
                  MachineConfigurationTypeNameContains: search,
                }),
                getDefaultItem: (model: any) => model.MachineConfigurationType,
                className: "col-xs-3",
              }),
              fieldHelpers.simpleStringField({
                key: "SlotNumber",
                label: "Slot Number",
                required: true,
                className: "col-xs-3",
                expressionProperties: {
                  "templateOptions.helpText": (
                    $viewValue: any,
                    $modelValue: any,
                    $scope: ITemplateScope
                  ) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        $scope.model?.MachineId
                      );
                    const slotNumber = machineConfigurationView?.SlotNumber;
                    return slotNumber ? `Previous: ${slotNumber}` : "";
                  },
                },
              }),
            ],
          },
          {
            className: "row",
            fieldGroup: [
              {
                key: "Section",
                name: "Section",
                type: "input",
                className: "col-xs-3",
                templateOptions: {
                  label: "Section",
                  required: true,
                },
                expressionProperties: {
                  "templateOptions.helpText": (
                    $viewValue,
                    $modelValue,
                    $scope
                  ) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        $scope.model?.MachineId
                      );
                    const section = machineConfigurationView?.Section;
                    return section ? `Previous: ${section}` : "";
                  },
                },
              },
              {
                key: "Bank",
                name: "Bank",
                type: "input",
                className: "col-xs-3",
                templateOptions: {
                  label: "Bank",
                  required: true,
                },
                expressionProperties: {
                  "templateOptions.helpText": (
                    $viewValue,
                    $modelValue,
                    $scope
                  ) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        $scope.model?.MachineId
                      );
                    const bank = machineConfigurationView?.Bank;
                    return bank ? `Previous: ${bank}` : "";
                  },
                },
              },
              {
                key: "Slot",
                name: "Slot",
                type: "input",
                className: "col-xs-3",
                templateOptions: {
                  label: "Slot",
                  required: true,
                },
                expressionProperties: {
                  "templateOptions.helpText": (
                    $viewValue,
                    $modelValue,
                    $scope
                  ) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        $scope.model?.MachineId
                      );
                    const slot = machineConfigurationView?.Slot;
                    return slot ? `Previous: ${slot}` : "";
                  },
                },
              },
            ],
          },
          {
            className: "row",
            fieldGroup: [
              {
                key: "BankSize",
                name: "BankSize",
                type: "input",
                className: "col-xs-3",
                templateOptions: {
                  label: "Bank Size",
                },
                expressionProperties: {
                  "templateOptions.helpText": (
                    $viewValue,
                    $modelValue,
                    $scope
                  ) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        $scope.model?.MachineId
                      );
                    const slot = machineConfigurationView?.BankSize;
                    return slot ? `Previous: ${slot}` : "";
                  },
                },
              },
              {
                key: "Position",
                name: "Position",
                type: "input",
                className: "col-xs-3",
                templateOptions: {
                  label: "Position",
                },
                expressionProperties: {
                  "templateOptions.helpText": (
                    $viewValue,
                    $modelValue,
                    $scope
                  ) => {
                    const machineConfigurationView =
                      this.getMachineConfigurationViewFromMachineId(
                        $scope.model?.MachineId
                      );
                    const slot = machineConfigurationView?.Position;
                    return slot ? `Previous: ${slot}` : "";
                  },
                },
              },
              this.fieldConfigurationHelperService.lookupField(
                this.lookupManagementService,
                {
                  key: "MachineBankTypeId",
                  objectName: lookupNames.bankTypeName,
                  defaultSort: "Name",
                  className: "col-xs-3",
                  expressionProperties: {
                    "templateOptions.helpText": (
                      $viewValue,
                      $modelValue,
                      $scope
                    ) => {
                      const machineConfigurationView =
                        this.getMachineConfigurationViewFromMachineId(
                          $scope.model?.MachineId
                        );
                      const slot = <Record>(
                        machineConfigurationView?.MachineBankType
                      );
                      return slot ? `Previous: ${slot.Name}` : "";
                    },
                  },
                }
              ),
              /* TODO - add client specific move fields here */
              {
                key: "copyDown",
                name: "copyDown",
                className: "col-xs-3",
                templateOptions: {
                  label: " ",
                },
                template: `
<div>
  <div class="pull-right">
    <div class="btn-group">
      <div class="btn btn-primary game-config-btn" ng-click="$emit('copyDown', model)">
        <i class="glyphicon glyphicon-arrow-down"></i>
      </div>
    </div>
  </div>
</div>
        `,
                wrapper: ["bootstrapLabel", "bootstrapHasError"],
              },
            ],
          },
        ],
        showHeader: true,
      }),
      {
        key: "Files",
        name: "Compliance Files",
        type: "recordFileUpload",
        templateOptions: {
          allowMultiple: true,
          label: "Compliance Files",
          group: "Compliance",
        },
      },
      {
        key: "Files",
        name: "Files",
        type: "recordFileUpload",
        templateOptions: {
          allowMultiple: true,
          label: "Software Compliance Notices",
          group: "SoftwareCompliance",
        },
      },
      {
        key: "Files",
        name: "Files",
        type: "recordFileUpload",
        templateOptions: {
          allowMultiple: true,
          label: "Internal Documentation",
          group: "InternalDocumentation",
        },
      },
      fieldHelpers.headerField({ headerText: "Notes", key: "NotesHeader" }),
      fieldHelpers.textAreaField({
        key: "Notes",
        name: "Notes",
        label: " ",
        rows: 5,
      }),
    ];
  }

  $onInit(): void {
    this.model = {
      Files: [],
      MachineChanges: (this.machineConfigurations ?? []).map((mc) => {
        return <IMachineMoveRequest>{
          MachineId: mc.MachineId,
          SlotNumber: mc.SlotNumber,
          MachineConfigurationTypeId: mc.MachineConfigurationTypeId,
          MachineConfigurationType: {
            Id: mc.MachineConfigurationTypeId,
            Name: mc.MachineConfigurationTypeName,
          },
        };
      }),
    };
    this.machineInfo = {
      machineConfigurations: cloneDeep(this.machineConfigurations),
    };
    this.initializeFormFields();
  }

  copyDown(modelClicked: IMachineMoveRequest) {
    const machineMoves = this.model.MachineChanges;
    const copyIndex = machineMoves.indexOf(modelClicked);
    for (let i = copyIndex + 1; i < machineMoves.length; i++) {
      const oldModel = machineMoves[i];
      const newModel: IMachineMoveRequest = {
        MachineId: machineMoves[i].MachineId,
        Section: modelClicked.Section,
        Bank: modelClicked.Bank,
        SlotNumber: oldModel.SlotNumber,
        MachineConfigurationTypeId: oldModel.MachineConfigurationTypeId,
        MachineConfigurationType: oldModel.MachineConfigurationType,
        MachineBankTypeId: modelClicked.MachineBankTypeId,
        MachineBankType: modelClicked.MachineBankType,
        BankSize: modelClicked.BankSize,
        /* add client specific fields that should be copied down when copy down button clicked */
      };
      this.recordUtils.assignId(newModel);
      machineMoves.splice(i, 1, newModel);
    }
  }

  submit(formViewController: FormViewController): IPromise<void> {
    return this.changeJournalModalService
      .getAndSetChangeJournalNote(this.model)
      .then(() =>
        this.formViewService.submitForm({
          url: `/api/machines/move`,
          formObject: this.model,
          formViewController,
          method: "post",
        })
      )
      .then(() => {
        this.$state.go("^.grid", undefined, { reload: true });
      });
  }

  async cancel(): Promise<void> {
    await this.$state.go("^.grid", undefined, { reload: true });
  }

  private getMachineConfigurationViewFromMachineId(machineId: number) {
    return this.machineInfo.machineConfigurations.find(
      (mc) => mc.MachineId === machineId
    );
  }
}
